import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { contrastingColor, getNameOrInitials } from '../../utils/utils';
import { Invitation } from '../../stores/reducers/invitations';
import { FEED_TYPES, ShotFeed } from '../../stores/reducers/shots';
import useShots from '../../contexts/shots/provider';
import { DragItem, DRAG_TYPE } from '../../stores/reducers/user-interface';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import VideoFeedContextMenu from './video-feed-context-menu';
import { setOpenContextMenuId } from '../../stores/actions/user-interface';

interface Props {
	invite: Invitation;
	isForShotModal?: boolean;
}

export default function VideoFeedFakeThumbnail(props: Props) {
	const { invite, isForShotModal = false } = props;

	const dispatch = useDispatch();

	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();

	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const onsiteUsers = useSelector((state: GreenroomState) => state.SessionReducer.onsiteUsers);
	const shotModalShot = useSelector((state: GreenroomState) => state.ShotsReducer.shotModalShot);

	const { currentShot } = useShots();

	const shotFeeds = useMemo(
		() => (isForShotModal ? shotModalShot.shot_data.shotFeeds : currentShot.shot_data.shotFeeds),
		[currentShot, shotModalShot, isForShotModal],
	);

	// The right click context menu requires a unique string id
	const contextMenuId = useMemo(() => `video-feed-${invite.profile.gr_user}`, [invite.profile.gr_user]);

	const isOnsiteUser = useMemo(
		() => (!invite.profile.gr_user ? false : !!onsiteUsers?.includes(invite.profile.gr_user)),
		[onsiteUsers, invite],
	);

	function getPosterImage(invite2: Invitation) {
		if (!invite2.profile) return '';
		if (invite2.profile.poster || invite2.profile.avatar) {
			return invite2.profile.poster || invite2.profile.avatar;
		}
		return '';
	}

	const inShot = useMemo(() => shotFeeds.some((feed: ShotFeed) => {
		if (feed.feedType === FEED_TYPES.EMAIL) {
			return feed.email === invite.profile.email;
		} if (feed.feedType === FEED_TYPES.PRESENTER) {
			return feed.presenter === invite.profile.gr_user;
		}

		return null;
	}), [invite, shotFeeds]);

	const dndProps = useMemo<DragItem>(() => {
		if (invite.profile.gr_user) {
			return {
				id: invite.profile.gr_user,
				type: DRAG_TYPE.FEED_PRESENTER,
			};
		}
		return {
			id: invite.profile.email,
			type: DRAG_TYPE.FEED_EMAIL,
		};
	}, [invite]);

	const { dragRef, isDragging } = useDragItem(dndProps, host);

	return (
		<>
			{!!invite.profile.gr_user && (
				<VideoFeedContextMenu
					clickCoordinates={rightClickCoords}
					contextMenuId={contextMenuId}
					socket={null}
					userId={invite.profile.gr_user}
				/>
			)}
					<React.Fragment>
						{isDragging && (<div className="video-thumb-drag-placeholder" />)}
						<div
							ref={dragRef}
							onContextMenu={(e) => {
								if (host) {
									e.preventDefault();
									setRightClickCoords([e.pageX, e.pageY]);
									dispatch(setOpenContextMenuId(contextMenuId));
								}
							}}
							onFocus={(e) => e.currentTarget.blur()}
							id={String(invite.code)}
							className={classNames('video-thumb', {
								'is-dragging': isDragging,
								'cursor-grab': host,
							})}
						>
							<div className="video-wrapper">
								<div className="video-overlay">
									<div className={classNames('poster')}>
										{invite && !!getPosterImage(invite)
											? <img className="poster-image" src={getPosterImage(invite)} alt={''} />
											: <div className="no-image-placeholder">
												<span style={{
													backgroundColor: meeting?.primary_color ? `#${meeting.primary_color}` : '#333',
													color: contrastingColor(meeting?.primary_color ?? '#333333'),
												}}>
													{getNameOrInitials(invite.profile, false)}
												</span>
											</div>
										}
									</div>
									{isOnsiteUser && (
										<div className="producer-label" data-testid="video-feed-fake-thumbnail-label">
											<span>ONSITE</span>
										</div>
									)}
								</div>

								<div className={classNames('video-overlay')}>
									<div className="video-overlay-bottom">
										<label className='font-size-14 font-weight-heavy'>{getNameOrInitials(invite.profile, true)}</label>
									</div>
								</div>

								<div className={classNames('selection-border', {
									'in-shot': inShot,
								})} />
							</div>
						</div>
					</React.Fragment>
					</>
	);
}
